import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, X, Layers, Building2, UsersRound, Download, Loader2,
  ShoppingCart, UserPlus, Undo2, Wrench, CheckCircle2, Ban,
  RotateCcw, Pencil, History, ChevronUp, ChevronDown, StickyNote, ArrowRightLeft, RefreshCw, MapPin,
} from 'lucide-react';
import { Card, CardContent, cn } from '../../ui';
import { useHistorial } from '../hooks/useHistorial';
import { useTiposNavegables } from '../hooks/useTiposNavegables';
import { rutaTipo, normAmbito, etiquetaAmbito } from '../utils/rutaTipo';
import { api } from '../api';
import { EVENT_TYPES } from '../data/historialMock';
import Paginacion from '../components/Paginacion';
import GuiaRapida from '../components/GuiaRapida';
import SearchableSelect from '../components/SearchableSelect';
import { fmtFecha, toAOA } from '../utils/exportarHistorial';
import * as XLSX from 'xlsx';

/**
 * Página: Historial global de eventos (sub-bloque III.7)
 *
 * Sustituye el placeholder de III.1. Replica el mockup v3:
 *   - Tab-bar de scope: Todos / GRUPOLAE / Colaboradores (con conteos)
 *   - Barra: búsqueda + filtro tipo de evento + tipo de activo + fechas
 *   - Tabla: Fecha · Scope · Tipo · Ref · Activo · Usuario · Registró
 *   - Export Excel (.xlsx) de los resultados filtrados
 *   - Paginación Stripe-style (reutiliza Paginacion de III.5)
 *   - Click en fila → abre el activo en su Lista (drawer de III.6)
 *
 * Pills/iconos de evento coherentes con Timeline (III.6) y Dashboard.
 */

const EV_META = {
  compra: { Icon: ShoppingCart, pill: 'bg-violet-100 text-violet-800', label: 'compra' },
  asignacion: { Icon: UserPlus, pill: 'bg-blue-100 text-blue-800', label: 'asignacion' },
  devolucion: { Icon: Undo2, pill: 'bg-emerald-100 text-emerald-800', label: 'devolucion' },
  reparacion_inicio: { Icon: Wrench, pill: 'bg-amber-100 text-amber-800', label: 'reparacion_inicio' },
  reparacion_fin: { Icon: CheckCircle2, pill: 'bg-emerald-100 text-emerald-800', label: 'reparacion_fin' },
  baja: { Icon: Ban, pill: 'bg-rose-100 text-rose-800', label: 'baja' },
  reactivacion: { Icon: RotateCcw, pill: 'bg-emerald-100 text-emerald-800', label: 'reactivacion' },
  edicion: { Icon: Pencil, pill: 'bg-slate-100 text-slate-600', label: 'edicion' },
  soft_delete: { Icon: Ban, pill: 'bg-rose-100 text-rose-800', label: 'soft_delete' },
  restore: { Icon: RotateCcw, pill: 'bg-emerald-100 text-emerald-800', label: 'restore' },
  // Faltaban: caían en el genérico (20/10, Fase 3).
  traspaso: { Icon: ArrowRightLeft, pill: 'bg-sky-100 text-sky-800', label: 'traspaso' },
  cambio_estado: { Icon: RefreshCw, pill: 'bg-slate-100 text-slate-700', label: 'cambio_estado' },
  nota: { Icon: StickyNote, pill: 'bg-amber-100 text-amber-800', label: 'nota' },
  movimiento: { Icon: MapPin, pill: 'bg-teal-100 text-teal-800', label: 'movimiento' },
};

const SCOPE_TABS = [
  { id: 'todos', label: 'Todos', Icon: Layers, countKey: 'todos' },
  { id: 'interno', label: 'GRUPOLAE', pill: 'Interno', Icon: Building2, countKey: 'interno' },
  { id: 'colab', label: 'Colaboradores', pill: 'Colab', Icon: UsersRound, countKey: 'colab' },
];

/**
 * Los filtros viven en la URL (Fase 3c), para que un enlace se pueda compartir y para que «Ver en el
 * historial» del panel de un activo llegue ya filtrado:
 *   scope · event_type · slug + tipo_scope (el tipo) · asset_id (con su tipo) · q · from · to
 *   · usuario (ámbito:id) · created_by
 * El tipo va por (slug, ámbito) y no por id: es lo que entiende el servidor, y no depende de que el
 * catálogo de tipos haya cargado.
 */
const AMBITOS_URL = ['interno', 'colab'];
function leerUsuario(v) {
  const m = /^(interno|colab):(\d+)$/.exec(v || '');
  return m ? { scope: m[1], id: m[2] } : null;
}

export default function HistorialGlobal() {
  const navigate = useNavigate();
  // Catálogo real para el filtro de tipo (y su traducción id → tipo).
  const { tipos, tipoPorId, loading: tiposLoading } = useTiposNavegables();
  const [searchParams, setSearchParams] = useSearchParams();
  // Lo que trae la URL al entrar (se lee UNA vez; después manda el estado y la URL lo sigue).
  const [urlInicial] = useState(() => {
    const g = (k) => searchParams.get(k) || '';
    const tipoScope = g('tipo_scope');
    const tipo = g('slug') && AMBITOS_URL.includes(tipoScope) ? { slug: g('slug'), scope: tipoScope } : null;
    return {
      scope: AMBITOS_URL.includes(g('scope')) ? g('scope') : 'todos',
      eventType: g('event_type'),
      tipo,
      // Un activo sólo vale con su tipo: los ids se repiten entre tablas.
      assetId: tipo && /^\d+$/.test(g('asset_id')) ? g('asset_id') : '',
      search: g('q'),
      from: g('from'),
      to: g('to'),
      usuario: leerUsuario(g('usuario')) ? g('usuario') : '',
      createdBy: /^\d+$/.test(g('created_by')) ? g('created_by') : '',
    };
  });

  const [scope, setScope] = useState(urlInicial.scope);
  const [eventType, setEventType] = useState(urlInicial.eventType);
  // Filtro de tipo: el par (slug, ámbito), que es lo que entiende el backend y lo que va en la URL.
  // El <select> trabaja con el id del catálogo; la traducción id ↔ par se hace aquí abajo.
  const [tipoSel, setTipoSel] = useState(urlInicial.tipo);
  // Filtro de activo (Fase 3c): id de la unidad DENTRO del tipo elegido. Sin tipo, no existe.
  const [assetId, setAssetId] = useState(urlInicial.assetId);
  const [activosTipo, setActivosTipo] = useState({ cargando: false, lista: [], error: null });
  const [from, setFrom] = useState(urlInicial.from);
  const [to, setTo] = useState(urlInicial.to);
  const [searchInput, setSearchInput] = useState(urlInicial.search);
  const [search, setSearch] = useState(urlInicial.search);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [orderBy, setOrderBy] = useState('fecha');
  const [orderDir, setOrderDir] = useState('desc');
  // Persona (titular) como «ámbito:id» (Fase 3c): el mismo id en personas y en
  // personas_colaboradoras son DOS personas, y el servidor filtra por las dos cosas.
  const [usuario, setUsuario] = useState(urlInicial.usuario);
  const [createdBy, setCreatedBy] = useState(urlInicial.createdBy);
  const [facetas, setFacetas] = useState({ usuarios: [], registradores: [] });

  // id del catálogo ↔ (slug, ámbito)
  const tipoId = useMemo(() => {
    if (!tipoSel) return '';
    const t = tipos.find((x) => x.slug === tipoSel.slug && normAmbito(x.scope) === tipoSel.scope);
    return t ? String(t.id) : '';
  }, [tipos, tipoSel]);
  const elegirTipo = (id) => {
    const t = id ? tipoPorId.get(String(id)) : null;
    setTipoSel(t ? { slug: t.slug, scope: normAmbito(t.scope) } : null);
    setAssetId('');
  };

  // Unidades del tipo elegido, para el filtro «Activo». El servidor sólo las da si se puede ver
  // ese tipo (403 si no): entonces no hay desplegable, sólo el aviso.
  useEffect(() => {
    let vivo = true;
    if (!tipoSel) { setActivosTipo({ cargando: false, lista: [], error: null }); return undefined; }
    setActivosTipo({ cargando: true, lista: [], error: null });
    api.get('/eventos/activos', { slug: tipoSel.slug, tipo_scope: tipoSel.scope })
      .then((l) => { if (vivo) setActivosTipo({ cargando: false, lista: Array.isArray(l) ? l : [], error: null }); })
      .catch((e) => {
        if (!vivo) return;
        const st = e?.response?.status;
        setActivosTipo({ cargando: false, lista: [], error: st === 403 ? 'Sin acceso a las unidades de este tipo.' : 'No se pudieron cargar las unidades.' });
      });
    return () => { vivo = false; };
  }, [tipoSel]);

  // Los filtros, a la URL (sin apilar entradas en el «atrás»).
  useEffect(() => {
    const p = new URLSearchParams();
    if (scope !== 'todos') p.set('scope', scope);
    if (eventType) p.set('event_type', eventType);
    if (tipoSel) { p.set('slug', tipoSel.slug); p.set('tipo_scope', tipoSel.scope); }
    if (tipoSel && assetId) p.set('asset_id', assetId);
    if (search) p.set('q', search);
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    if (usuario) p.set('usuario', usuario);
    if (createdBy) p.set('created_by', createdBy);
    if (p.toString() !== searchParams.toString()) setSearchParams(p, { replace: true });
  }, [scope, eventType, tipoSel, assetId, search, from, to, usuario, createdBy, searchParams, setSearchParams]);

  // Facetas de Usuario/Registró: solo personas presentes en el historial.
  // Se cargan una vez al montar; si falla, los selects quedan con solo "todos".
  useEffect(() => {
    let vivo = true;
    api
      .get('/eventos/facetas')
      .then((r) => {
        if (vivo) setFacetas(r || { usuarios: [], registradores: [] });
      })
      .catch(() => {
        /* silencioso: el resto del historial sigue funcionando */
      });
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const usuarioSel = useMemo(() => leerUsuario(usuario), [usuario]);

  const params = useMemo(
    () => ({
      scope,
      event_type: eventType || undefined,
      // Los DOS parámetros que identifican un tipo en el backend: el slug y su ámbito. El ámbito
      // DEL TIPO es independiente de la pestaña de arriba: con la pestaña en "Todos", filtrar por
      // un slug duplicado traía los eventos de los dos gemelos mezclados.
      slug: tipoSel ? tipoSel.slug : undefined,
      tipo_scope: tipoSel ? tipoSel.scope : undefined,
      asset_id: tipoSel && assetId ? assetId : undefined,
      search: search || undefined,
      from: from || undefined,
      to: to || undefined,
      user_id: usuarioSel ? usuarioSel.id : undefined,
      user_scope: usuarioSel ? usuarioSel.scope : undefined,
      created_by: createdBy || undefined,
      orderBy,
      orderDir,
      page,
      pageSize,
    }),
    [
      scope, eventType, tipoSel, assetId, search, from, to, usuarioSel, createdBy,
      orderBy, orderDir, page, pageSize,
    ]
  );

  const toggleSort = (col) => {
    if (orderBy === col) setOrderDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setOrderBy(col);
      setOrderDir('asc');
    }
    setPage(1);
  };

  const SortHeader = ({ col, children }) => (
    <th
      className="px-4 py-2.5 font-medium cursor-pointer select-none hover:text-slate-700"
      onClick={() => toggleSort(col)}
      aria-sort={
        orderBy === col ? (orderDir === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {orderBy === col &&
          (orderDir === 'asc' ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          ))}
      </span>
    </th>
  );

  const { data, pagination, counts, allFiltered, loading, error, isMock } =
    useHistorial(params);

  function exportExcel() {
    const ws = XLSX.utils.aoa_to_sheet(toAOA(allFiltered));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historial');
    XLSX.writeFile(wb, `historial-inventario-${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  function resetFiltros() {
    setEventType('');
    setTipoSel(null);
    setAssetId('');
    setFrom('');
    setTo('');
    setSearchInput('');
    setUsuario('');
    setCreatedBy('');
    setPage(1);
  }
  const hayFiltros = eventType || tipoSel || from || to || search || usuario || createdBy;

  // Opciones de persona con su ámbito: «ámbito:id». Las colaboradoras, marcadas, para distinguir a
  // dos personas con el mismo nombre en los dos ámbitos.
  const opcionesUsuario = useMemo(
    () => (facetas.usuarios || []).map((p) => ({
      id: `${p.scope}:${p.id}`,
      nombre: p.scope === 'colab' ? `${p.nombre} · ${etiquetaAmbito('colab')}` : p.nombre,
    })),
    [facetas.usuarios]
  );

  // Opciones de activo. Si el de la URL ya no existe (borrado), se enseña con la referencia que
  // guardan sus eventos, para que el filtro se entienda.
  const opcionesActivo = useMemo(() => {
    const l = activosTipo.lista;
    if (assetId && !activosTipo.cargando && !l.some((a) => String(a.id) === String(assetId))) {
      const ev = (data || []).find((r) => String(r.asset_id) === String(assetId));
      return [{ id: Number(assetId), nombre: `${ev && ev.referencia ? ev.referencia : `#${assetId}`} (ya no existe)` }, ...l];
    }
    return l;
  }, [activosTipo, assetId, data]);

  return (
    <div className="px-6 py-5">
      {/* HEADER */}
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
        <h1 className="text-xl font-semibold text-slate-900 leading-tight">
          Historial global
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Todos los eventos registrados ·{' '}
          <span className="font-medium text-slate-700">{counts.todos}</span> entradas
          {isMock && (
            <span className="ml-2 text-[11px] text-amber-600 font-medium">
              · datos de demostración (backend pendiente)
            </span>
          )}
        </p>
        </div>
        <GuiaRapida seccion="historial" />
      </div>

      {/* TAB-BAR SCOPE */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-3">
        {SCOPE_TABS.map(({ id, label, pill, Icon, countKey }) => {
          const active = scope === id;
          return (
            <button
              key={id}
              onClick={() => {
                setScope(id);
                setPage(1);
              }}
              className={cn(
                'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                active
                  ? 'border-[hsl(var(--brand-light))] text-[hsl(var(--brand-light))]'
                  : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
              {pill && (
                <span
                  className={cn(
                    'text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded',
                    active ? 'bg-[hsl(var(--brand-light))] text-white' : 'bg-slate-100 text-slate-500'
                  )}
                >
                  {pill}
                </span>
              )}
              <span className="text-xs text-slate-400">({counts[countKey]})</span>
            </button>
          );
        })}
      </div>

      {/* FILTROS */}
      <Card className="mb-3">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Buscar por referencia, usuario, motivo o notas…"
                className="w-full pl-9 pr-9 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Limpiar"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <select
              value={eventType}
              onChange={(e) => {
                setEventType(e.target.value);
                setPage(1);
              }}
              className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Todos los eventos</option>
              {EVENT_TYPES.map((ev) => (
                <option key={ev} value={ev}>
                  {ev}
                </option>
              ))}
            </select>

            {/* Tipos del CATÁLOGO REAL (antes: lista fija de historialMock,
                que ofrecía tipos inexistentes y omitía los reales). El
                <option> lleva el id —lo único único— y la traducción a
                (slug, ámbito) la hace elegirTipo, en un solo sitio. */}
            <select
              value={tipoId || (tipoSel ? '__url' : '')}
              onChange={(e) => {
                if (e.target.value === '__url') return;
                elegirTipo(e.target.value);
                setPage(1);
              }}
              className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              disabled={tiposLoading}
            >
              <option value="">Todos los tipos</option>
              {/* Un tipo que llega por la URL y no está entre los que esta persona puede ver: el
                  filtro se aplica igual, así que se enseña tal cual en vez de «Todos los tipos». */}
              {tipoSel && !tipoId && !tiposLoading && (
                <option value="__url">{tipoSel.slug} · {etiquetaAmbito(tipoSel.scope)}</option>
              )}
              {tipos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre} · {etiquetaAmbito(t.scope)}
                </option>
              ))}
            </select>

            {/* Activo (Fase 3c): sólo con un tipo elegido. Busca por referencia y por los campos de
                texto del tipo (la matrícula en el coche), sin tildes y en cualquier parte. */}
            {tipoSel && (activosTipo.error ? (
              <span className="text-xs text-slate-500 self-center">{activosTipo.error}</span>
            ) : (
              <div className="w-56" aria-label="Activo">
                <SearchableSelect
                  value={assetId}
                  onChange={(v) => {
                    setAssetId(v);
                    setPage(1);
                  }}
                  options={opcionesActivo}
                  loading={activosTipo.cargando}
                  placeholder="Activo: todos"
                />
              </div>
            ))}

            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setPage(1);
              }}
              className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white"
              aria-label="Desde"
            />
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
                setPage(1);
              }}
              className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white"
              aria-label="Hasta"
            />

            {/* Personas: con buscador (sin tildes, en cualquier parte del nombre), como en asignar y
                en el alta (Fase 3c). La X del desplegable vuelve a «todos». */}
            <div className="w-56" aria-label="Usuario">
              <SearchableSelect
                value={usuario}
                onChange={(v) => {
                  setUsuario(v);
                  setPage(1);
                }}
                options={opcionesUsuario}
                placeholder="Usuario: todos"
              />
            </div>

            <div className="w-56" aria-label="Registró">
              <SearchableSelect
                value={createdBy}
                onChange={(v) => {
                  setCreatedBy(v);
                  setPage(1);
                }}
                options={facetas.registradores}
                placeholder="Registró: todos"
              />
            </div>

            {hayFiltros && (
              <button
                onClick={resetFiltros}
                className="text-xs text-slate-500 hover:text-slate-800 px-2 py-2"
              >
                Limpiar
              </button>
            )}

            <button
              onClick={exportExcel}
              className="px-3 py-2 border border-slate-200 rounded-md text-sm flex items-center gap-2 hover:bg-slate-50 ml-auto"
            >
              <Download className="w-3.5 h-3.5" />
              Exportar Excel
            </button>
          </div>
        </CardContent>
      </Card>

      {/* TABLA */}
      <Card>
        {error && (
          <CardContent className="p-4 text-sm text-rose-600">
            Error al cargar el historial: {error.message}
          </CardContent>
        )}
        {!error && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-100 text-left">
                  <SortHeader col="fecha">Fecha</SortHeader>
                  <SortHeader col="scope">Scope</SortHeader>
                  <SortHeader col="event_type">Tipo</SortHeader>
                  <SortHeader col="referencia">Ref</SortHeader>
                  <th className="px-4 py-2.5 font-medium">Activo</th>
                  <th className="px-4 py-2.5 font-medium">Usuario</th>
                  <th className="px-4 py-2.5 font-medium">Registró</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-300 mx-auto" />
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">
                      <History className="w-7 h-7 text-slate-300 mx-auto mb-2" />
                      No hay eventos que coincidan con los filtros.
                    </td>
                  </tr>
                ) : (
                  data.map((r) => {
                    const m = EV_META[r.event_type] || {
                      Icon: History, pill: 'bg-slate-100 text-slate-600', label: r.event_type,
                    };
                    const Icon = m.Icon;
                    return (
                      <tr
                        key={r.id}
                        // La fila YA trae su ámbito (`e.scope` de
                        // inv_asset_events) y antes se tiraba: con un slug
                        // duplicado el enlace caía en el gemelo equivocado y
                        // abría otro activo con el mismo id, o un 404.
                        onClick={() =>
                          navigate(rutaTipo(r.scope, r.asset_type_slug, r.asset_id))
                        }
                        className="hover:bg-slate-50 cursor-pointer"
                      >
                        <td className="px-4 py-3 text-slate-700 tabular-nums whitespace-nowrap">
                          {fmtFecha(r.fecha)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'text-[11px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded',
                              r.scope === 'colab'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-600'
                            )}
                          >
                            {r.scope === 'colab' ? 'Colab' : 'Interno'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium',
                              m.pill
                            )}
                          >
                            <Icon className="w-3 h-3" />
                            {m.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs font-semibold tabular-nums text-slate-900">
                          {r.referencia}
                        </td>
                        <td className="px-4 py-3 text-slate-700">{r.asset_nombre}</td>
                        <td className="px-4 py-3 text-slate-700">
                          {r.user_nombre || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500">
                          {r.creator_nombre || '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-3 border-t border-slate-100">
          <Paginacion
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onPageChange={setPage}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(1);
            }}
          />
        </div>
      </Card>
    </div>
  );
}
